<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useEmitter } from 'dashboard/composables/emitter';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import ConversationApi from 'dashboard/api/inbox/conversation';
import LiveReportsAPI from 'dashboard/api/liveReports';
import ReportsAPI from 'dashboard/api/reports';
import SummaryReportsAPI from 'dashboard/api/summaryReports';
import CSATReportsAPI from 'dashboard/api/csatReports';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const { accountScopedRoute } = useAccount();

const currentUser = useMapGetter('getCurrentUser');
const agentStatus = useMapGetter('agents/getAgentStatus');

const loading = ref(true);
const assuming = ref(false);
const assumingId = ref(null);
const refreshing = ref(false);
const period = ref('daily'); // daily | monthly | yearly
const searchQuery = ref('');
const queueItems = ref([]);
const aiItems = ref([]);

const metrics = ref({
  inProgress: 0,
  waitingReply: 0,
  idle24h: 0,
  waitingCustomer: 0,
  csatTotal: 0,
  csatRatings: 0,
  conversationsCount: 0,
  resolutionsCount: 0,
  avgReplyTime: 0,
  avgResolutionTime: 0,
  unassignedCount: 0,
  withAi: 0,
});

const sessionStartedAt = ref(Date.now());
const nowTick = ref(Date.now());
let tickTimer = null;
let liveTimer = null;
let reportsTimer = null;
let liveRefreshTimer = null;
let liveRefreshInFlight = false;

const startOfLocalDayUnix = (date = new Date()) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return Math.floor(d.getTime() / 1000);
};

const dayBounds = computed(() => {
  const end = Math.floor(Date.now() / 1000);
  let start;
  if (period.value === 'yearly') {
    const d = new Date();
    d.setMonth(0, 1);
    d.setHours(0, 0, 0, 0);
    start = Math.floor(d.getTime() / 1000);
  } else if (period.value === 'monthly') {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    start = Math.floor(d.getTime() / 1000);
  } else {
    start = startOfLocalDayUnix();
  }
  return { since: start, until: end };
});

const periodLabelKey = computed(() => {
  if (period.value === 'yearly') return 'AGENT_DESK.PERIOD.YEARLY';
  if (period.value === 'monthly') return 'AGENT_DESK.PERIOD.MONTHLY';
  return 'AGENT_DESK.PERIOD.DAILY';
});

const csatScore = computed(() => {
  if (!metrics.value.csatRatings) return null;
  return {
    rated: metrics.value.csatRatings,
    sent: metrics.value.csatTotal,
  };
});

const presence = computed(() => {
  const status = currentUser.value?.availability_status || 'offline';
  const elapsedMin = Math.max(
    0,
    Math.floor((nowTick.value - sessionStartedAt.value) / 60000)
  );
  const online = status === 'online' ? elapsedMin : 0;
  const busy = status === 'busy' ? elapsedMin : 0;
  const away = status === 'away' || status === 'snooze' ? elapsedMin : 0;
  const offline = status === 'offline' ? elapsedMin : 0;
  const total = Math.max(1, online + busy + away + offline);
  return {
    status,
    online,
    busy,
    away,
    offline,
    onlinePct: Math.round((online / total) * 100),
    busyPct: Math.round((busy / total) * 100),
    awayPct: Math.round((away / total) * 100),
    offlinePct: Math.round((offline / total) * 100),
  };
});

const formatSeconds = seconds => {
  const s = Number(seconds) || 0;
  if (s <= 0) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return `${h}h ${m}min`;
};

const formatWait = value => {
  const ms = toMs(value);
  if (!ms) return '';
  const mins = Math.max(0, Math.floor((Date.now() - ms) / 60000));
  if (mins < 1) return t('AGENT_DESK.QUEUE.JUST_NOW');
  if (mins < 60) return t('AGENT_DESK.QUEUE.WAIT_MIN', { count: mins });
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return t('AGENT_DESK.QUEUE.WAIT_HOUR', { hours: h, minutes: m });
};

const formatHandling = value => {
  const ms = toMs(value);
  if (!ms) return t('AGENT_DESK.AI_QUEUE.HANDLING_JUST_NOW');
  const mins = Math.max(0, Math.floor((Date.now() - ms) / 60000));
  if (mins < 1) return t('AGENT_DESK.AI_QUEUE.HANDLING_JUST_NOW');
  const label =
    mins < 60
      ? `${mins} min`
      : `${Math.floor(mins / 60)}h ${mins % 60}min`;
  return t('AGENT_DESK.AI_QUEUE.HANDLING_SINCE', { label });
};

/** Chatwoot conversations index wraps payload in `{ data: { meta, payload } }`. */
const unwrapConversations = responseData => {
  const nested = responseData?.data;
  if (nested && (nested.payload || nested.meta)) {
    return {
      list: nested.payload || [],
      meta: nested.meta || {},
    };
  }
  return {
    list: responseData?.payload || [],
    meta: responseData?.meta || {},
  };
};

const toMs = value => {
  if (value == null || value === '' || value === 0) return 0;
  if (typeof value === 'number') {
    return value < 1e12 ? value * 1000 : value;
  }
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const contactName = conversation => {
  const sender = conversation?.meta?.sender || {};
  return (
    sender.name ||
    sender.phone_number ||
    sender.email ||
    t('AGENT_DESK.QUEUE.UNKNOWN_CONTACT')
  );
};

const contactPhone = conversation => {
  const sender = conversation?.meta?.sender || {};
  return sender.phone_number || sender.identifier || '';
};

const lastMessagePreview = conversation => {
  const msg =
    conversation?.last_non_activity_message ||
    (conversation?.messages && conversation.messages[0]) ||
    null;
  if (!msg) return t('AGENT_DESK.QUEUE.NO_PREVIEW');
  if (msg.content) return msg.content;
  if (msg.attachments?.length) return t('AGENT_DESK.QUEUE.ATTACHMENT');
  return t('AGENT_DESK.QUEUE.NO_PREVIEW');
};

const mapQueueItem = conversation => ({
  id: conversation.id,
  name: contactName(conversation),
  phone: contactPhone(conversation),
  preview: lastMessagePreview(conversation),
  unread: conversation.unread_count || 0,
  waitingLabel: formatWait(
    conversation.waiting_since || conversation.last_activity_at
  ),
  inboxId: conversation.inbox_id,
});

const mapAiItem = conversation => ({
  id: conversation.id,
  name: contactName(conversation),
  phone: contactPhone(conversation),
  preview: lastMessagePreview(conversation),
  unread: conversation.unread_count || 0,
  handlingLabel: formatHandling(
    conversation.waiting_since || conversation.last_activity_at
  ),
  inboxId: conversation.inbox_id,
});

const loadOpenMetrics = async () => {
  const { data } = await ConversationApi.get({
    status: 'open',
    assigneeType: 'all',
    page: 1,
  });
  const { list, meta } = unwrapConversations(data);
  const now = Date.now();
  const dayMs = 24 * 3600 * 1000;

  let waitingReply = 0;
  let idle24h = 0;
  let waitingCustomer = 0;

  list.forEach(c => {
    const waitingSince = c.waiting_since || c.meta?.waiting_since;
    const lastActivity = toMs(c.last_activity_at || c.timestamp);
    if (waitingSince) waitingReply += 1;
    else waitingCustomer += 1;
    if (lastActivity && now - lastActivity > dayMs) idle24h += 1;
  });

  metrics.value.inProgress = meta.all_count ?? list.length;
  metrics.value.waitingReply = waitingReply;
  metrics.value.waitingCustomer = waitingCustomer;
  metrics.value.idle24h = idle24h;
  if (meta.unassigned_count != null) {
    metrics.value.unassignedCount = meta.unassigned_count;
  }
};

const loadQueue = async () => {
  const { data } = await ConversationApi.get({
    status: 'open',
    assigneeType: 'unassigned',
    page: 1,
    sortBy: 'last_activity_at_asc',
  });
  const { list, meta } = unwrapConversations(data);
  queueItems.value = list.map(mapQueueItem);
  if (meta.unassigned_count != null) {
    metrics.value.unassignedCount = meta.unassigned_count;
  } else {
    metrics.value.unassignedCount = list.length;
  }
};

const loadAiQueue = async () => {
  const { data } = await ConversationApi.get({
    status: 'pending',
    assigneeType: 'all',
    page: 1,
    sortBy: 'last_activity_at_asc',
  });
  const { list, meta } = unwrapConversations(data);
  aiItems.value = list.map(mapAiItem);
  metrics.value.withAi = meta.all_count ?? list.length;
};

const loadLive = async () => {
  const { data } = await LiveReportsAPI.getConversationMetric();
  if (data?.open != null) {
    metrics.value.inProgress = data.open;
  }
  if (data?.unassigned != null) {
    metrics.value.unassignedCount = data.unassigned;
  }
};

const loadReports = async () => {
  const { since, until } = dayBounds.value;
  const [summaryRes, csatRes, agentSummaryRes] = await Promise.all([
    ReportsAPI.getSummary(since, until, 'account'),
    CSATReportsAPI.getMetrics({ from: since, to: until }),
    SummaryReportsAPI.getAgentReports({ since, until }),
  ]);

  const summary = summaryRes?.data || {};
  metrics.value.conversationsCount =
    summary.conversations_count || summary?.data?.conversations_count || 0;
  metrics.value.resolutionsCount =
    summary.resolutions_count || summary?.data?.resolutions_count || 0;

  const csat = csatRes?.data || {};
  metrics.value.csatTotal = csat.total_count || 0;
  metrics.value.csatRatings = csat.ratings_count || 0;

  const agents = agentSummaryRes?.data || [];
  const mine = Array.isArray(agents)
    ? agents.find(a => Number(a.id) === Number(currentUser.value?.id))
    : null;
  metrics.value.avgReplyTime =
    mine?.avg_reply_time || mine?.avg_first_response_time || 0;
  metrics.value.avgResolutionTime = mine?.avg_resolution_time || 0;
};

const refreshLive = async () => {
  if (liveRefreshInFlight) return;
  liveRefreshInFlight = true;
  try {
    await Promise.all([
      loadOpenMetrics().catch(() => null),
      loadQueue().catch(() => null),
      loadAiQueue().catch(() => null),
      loadLive().catch(() => null),
    ]);
  } finally {
    liveRefreshInFlight = false;
    loading.value = false;
  }
};

const scheduleLiveRefresh = (delay = 400) => {
  if (liveRefreshTimer) clearTimeout(liveRefreshTimer);
  liveRefreshTimer = setTimeout(() => {
    liveRefreshTimer = null;
    refreshLive();
  }, delay);
};

const refresh = async ({ silent = false } = {}) => {
  if (!silent) refreshing.value = true;
  try {
    await Promise.all([
      refreshLive(),
      loadReports().catch(() => null),
    ]);
  } finally {
    loading.value = false;
    refreshing.value = false;
  }
};

const openConversation = conversationId => {
  router.push(
    accountScopedRoute('inbox_conversation', {
      conversation_id: conversationId,
    })
  );
};

const assumeConversation = async conversation => {
  if (assuming.value) return;
  assuming.value = true;
  assumingId.value = conversation.id;
  try {
    await store.dispatch('assignAgent', {
      conversationId: conversation.id,
      agentId: currentUser.value.id,
      assigneeType: 'User',
    });
    useAlert(t('AGENT_DESK.ASSUMED'));
    queueItems.value = queueItems.value.filter(i => i.id !== conversation.id);
    metrics.value.unassignedCount = Math.max(
      0,
      metrics.value.unassignedCount - 1
    );
    openConversation(conversation.id);
  } catch (e) {
    useAlert(t('AGENT_DESK.ASSUME_ERROR'));
  } finally {
    assuming.value = false;
    assumingId.value = null;
  }
};

const assumeFromAi = async conversation => {
  if (assuming.value) return;
  assuming.value = true;
  assumingId.value = conversation.id;
  try {
    // Assigning a human agent already clears ai_assignee and moves the
    // conversation out of "pending" (see Conversations::AssignmentService#
    // open_when_assigned_to_human), which is what actually stops the AI
    // from responding - it only acts while status stays pending.
    await store.dispatch('assignAgent', {
      conversationId: conversation.id,
      agentId: currentUser.value.id,
      assigneeType: 'User',
    });
    useAlert(t('AGENT_DESK.AI_QUEUE.ASSUMED'));
    aiItems.value = aiItems.value.filter(i => i.id !== conversation.id);
    metrics.value.withAi = Math.max(0, metrics.value.withAi - 1);
    openConversation(conversation.id);
  } catch (e) {
    useAlert(t('AGENT_DESK.ASSUME_ERROR'));
  } finally {
    assuming.value = false;
    assumingId.value = null;
  }
};

const assumeNext = async () => {
  if (assuming.value) return;
  if (queueItems.value.length) {
    await assumeConversation(queueItems.value[0]);
    return;
  }
  try {
    const { data } = await ConversationApi.get({
      status: 'open',
      assigneeType: 'unassigned',
      page: 1,
      sortBy: 'last_activity_at_asc',
    });
    const { list } = unwrapConversations(data);
    if (!list.length) {
      useAlert(t('AGENT_DESK.QUEUE_EMPTY'));
      return;
    }
    await assumeConversation(mapQueueItem(list[0]));
  } catch (e) {
    useAlert(t('AGENT_DESK.ASSUME_ERROR'));
  }
};

const goConversations = () => {
  router.push(accountScopedRoute('home'));
};

const onSearch = () => {
  const q = searchQuery.value.trim();
  if (!q) {
    goConversations();
    return;
  }
  router.push({
    ...accountScopedRoute('contacts_dashboard_index'),
    query: { page: 1, search: q },
  });
};

watch(period, () => {
  loadReports().catch(() => null);
});

// ActionCable already emits this on message/conversation/assignee changes
useEmitter('fetch_conversation_stats', () => scheduleLiveRefresh(300));
useEmitter(BUS_EVENTS.WEBSOCKET_RECONNECT, () => scheduleLiveRefresh(500));

onMounted(async () => {
  sessionStartedAt.value = Date.now();
  await store.dispatch('agents/get');
  await refresh();
  tickTimer = setInterval(() => {
    nowTick.value = Date.now();
  }, 30000);
  // Backup polling if a websocket event is missed
  liveTimer = setInterval(() => refreshLive(), 15000);
  reportsTimer = setInterval(() => loadReports().catch(() => null), 120000);
});

onUnmounted(() => {
  if (tickTimer) clearInterval(tickTimer);
  if (liveTimer) clearInterval(liveTimer);
  if (reportsTimer) clearInterval(reportsTimer);
  if (liveRefreshTimer) clearTimeout(liveRefreshTimer);
});
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 overflow-y-auto bg-sn-bg">
    <header
      class="sticky top-0 z-10 flex flex-wrap items-center gap-3 px-6 py-4 bg-white/90 backdrop-blur border-b border-sn-border"
    >
      <div class="min-w-0 flex-1">
        <p class="text-xs text-sn-muted">
          {{ t('AGENT_DESK.BREADCRUMB') }}
        </p>
        <h1 class="text-xl font-semibold text-sn-ink truncate">
          {{ t('AGENT_DESK.TITLE') }}
        </h1>
      </div>

      <form
        class="flex items-center gap-2 flex-1 min-w-[200px] max-w-md"
        @submit.prevent="onSearch"
      >
        <input
          v-model="searchQuery"
          type="search"
          class="w-full h-9 px-3 rounded-lg border border-sn-border bg-sn-bg text-sm text-sn-ink outline-none focus:border-sn-primary"
          :placeholder="t('AGENT_DESK.SEARCH_PLACEHOLDER')"
        />
      </form>

      <Button
        size="sm"
        color="slate"
        variant="faded"
        icon="i-lucide-refresh-cw"
        :is-loading="refreshing"
        @click="refresh()"
      />
      <Button
        size="sm"
        color="amber"
        solid
        icon="i-lucide-user-round-plus"
        :label="t('AGENT_DESK.ASSUME_NEXT')"
        :is-loading="assuming && !assumingId"
        @click="assumeNext"
      />
      <Button
        size="sm"
        color="slate"
        variant="faded"
        :label="t('AGENT_DESK.OPEN_CONVERSATIONS')"
        @click="goConversations"
      />
    </header>

    <div class="p-6 flex flex-col gap-6">
      <!-- KPI row -->
      <section class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <div
          class="rounded-2xl bg-white border border-sn-border p-4 shadow-[0_1px_2px_rgba(26,30,38,0.04)]"
        >
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-sm text-sn-muted">
                {{ t('AGENT_DESK.KPI.IN_PROGRESS') }}
              </p>
              <p class="mt-2 text-3xl font-semibold text-sn-ink">
                {{ loading ? '—' : metrics.inProgress }}
              </p>
            </div>
            <span
              class="grid place-content-center size-10 rounded-xl bg-sn-primary/10 text-sn-primary i-lucide-message-circle text-xl"
            />
          </div>
        </div>

        <div
          class="rounded-2xl bg-white border border-sn-border p-4 shadow-[0_1px_2px_rgba(26,30,38,0.04)]"
        >
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-sm text-sn-muted">
                {{ t('AGENT_DESK.KPI.WAITING_REPLY') }}
              </p>
              <p class="mt-2 text-3xl font-semibold text-sn-ink">
                {{ loading ? '—' : metrics.waitingReply }}
              </p>
            </div>
            <span
              class="grid place-content-center size-10 rounded-xl bg-sn-accent/10 text-sn-accent i-lucide-message-square-dot text-xl"
            />
          </div>
        </div>

        <div
          class="rounded-2xl bg-white border border-sn-border p-4 shadow-[0_1px_2px_rgba(26,30,38,0.04)]"
        >
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-sm text-sn-muted">
                {{ t('AGENT_DESK.KPI.IDLE_24H') }}
              </p>
              <p class="mt-2 text-3xl font-semibold text-sn-ink">
                {{ loading ? '—' : metrics.idle24h }}
              </p>
            </div>
            <span
              class="grid place-content-center size-10 rounded-xl bg-red-500/10 text-red-500 i-lucide-circle-alert text-xl"
            />
          </div>
        </div>

        <div
          class="rounded-2xl bg-white border border-sn-border p-4 shadow-[0_1px_2px_rgba(26,30,38,0.04)]"
        >
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-sm text-sn-muted">
                {{ t('AGENT_DESK.KPI.QUEUE') }}
              </p>
              <p class="mt-2 text-3xl font-semibold text-sn-ink">
                {{ loading ? '—' : metrics.unassignedCount }}
              </p>
              <p class="mt-1 text-xs text-sn-muted">
                {{ t('AGENT_DESK.KPI.WAITING_CUSTOMER') }}:
                {{ loading ? '—' : metrics.waitingCustomer }}
              </p>
            </div>
            <span
              class="grid place-content-center size-10 rounded-xl bg-emerald-500/10 text-emerald-600 i-lucide-list-ordered text-xl"
            />
          </div>
        </div>

        <div
          class="rounded-2xl bg-white border border-sn-border p-4 shadow-[0_1px_2px_rgba(26,30,38,0.04)]"
        >
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-sm text-sn-muted">
                {{ t('AGENT_DESK.KPI.WITH_AI') }}
              </p>
              <p class="mt-2 text-3xl font-semibold text-sn-ink">
                {{ loading ? '—' : metrics.withAi }}
              </p>
            </div>
            <span
              class="grid place-content-center size-10 rounded-xl bg-violet-500/10 text-violet-600 i-lucide-bot text-xl"
            />
          </div>
        </div>
      </section>

      <!-- Conversas sendo atendidas pela IA agora -->
      <section
        class="rounded-2xl bg-white border border-sn-border p-5 shadow-[0_1px_2px_rgba(26,30,38,0.04)]"
      >
        <div class="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 class="font-semibold text-sn-ink">
              {{ t('AGENT_DESK.AI_QUEUE.TITLE') }}
            </h2>
            <p class="text-xs text-sn-muted mt-0.5">
              {{
                t('AGENT_DESK.AI_QUEUE.SUBTITLE', {
                  count: metrics.withAi,
                })
              }}
            </p>
          </div>
          <span
            class="inline-flex items-center justify-center min-w-8 h-8 px-2 rounded-full bg-violet-500 text-white text-sm font-semibold"
          >
            {{ metrics.withAi }}
          </span>
        </div>

        <div
          v-if="!aiItems.length"
          class="grid place-content-center min-h-[120px] text-sm text-sn-muted"
        >
          {{ t('AGENT_DESK.AI_QUEUE.EMPTY_LIST') }}
        </div>

        <ul v-else class="divide-y divide-sn-border">
          <li
            v-for="item in aiItems"
            :key="item.id"
            class="flex flex-col sm:flex-row sm:items-center gap-3 py-3 first:pt-0 last:pb-0"
          >
            <button
              type="button"
              class="flex items-start gap-3 min-w-0 flex-1 text-left"
              @click="openConversation(item.id)"
            >
              <span
                class="grid place-content-center size-10 rounded-full bg-violet-500/10 text-violet-600 font-semibold shrink-0 i-lucide-bot text-lg"
              />
              <span class="min-w-0 flex-1">
                <span class="flex items-center gap-2 flex-wrap">
                  <span class="font-medium text-sn-ink truncate">{{
                    item.name
                  }}</span>
                  <span
                    v-if="item.unread"
                    class="inline-grid place-items-center min-w-5 h-5 px-1 rounded-full bg-sn-accent text-white text-xxs font-medium"
                  >
                    {{ item.unread > 99 ? '99+' : item.unread }}
                  </span>
                </span>
                <span
                  v-if="item.phone"
                  class="block text-xs text-sn-muted truncate"
                  >{{ item.phone }}</span
                >
                <span class="block text-sm text-sn-muted truncate mt-0.5">{{
                  item.preview
                }}</span>
                <span
                  v-if="item.handlingLabel"
                  class="block text-xs text-violet-600 mt-0.5"
                  >{{ item.handlingLabel }}</span
                >
              </span>
            </button>
            <div class="flex items-center gap-2 shrink-0 sm:pl-2">
              <Button
                size="sm"
                color="slate"
                variant="faded"
                :label="t('AGENT_DESK.AI_QUEUE.OPEN')"
                @click="openConversation(item.id)"
              />
              <Button
                size="sm"
                color="blue"
                solid
                :label="t('AGENT_DESK.AI_QUEUE.ASSUME')"
                :is-loading="assuming && assumingId === item.id"
                @click="assumeFromAi(item)"
              />
            </div>
          </li>
        </ul>
      </section>

      <!-- Fila de clientes -->
      <section
        class="rounded-2xl bg-white border border-sn-border p-5 shadow-[0_1px_2px_rgba(26,30,38,0.04)]"
      >
        <div class="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 class="font-semibold text-sn-ink">
              {{ t('AGENT_DESK.QUEUE.TITLE') }}
            </h2>
            <p class="text-xs text-sn-muted mt-0.5">
              {{
                t('AGENT_DESK.QUEUE.SUBTITLE', {
                  count: metrics.unassignedCount,
                })
              }}
            </p>
          </div>
          <span
            class="inline-flex items-center justify-center min-w-8 h-8 px-2 rounded-full bg-sn-accent text-white text-sm font-semibold"
          >
            {{ metrics.unassignedCount }}
          </span>
        </div>

        <div
          v-if="!queueItems.length"
          class="grid place-content-center min-h-[120px] text-sm text-sn-muted"
        >
          {{ t('AGENT_DESK.QUEUE.EMPTY_LIST') }}
        </div>

        <ul v-else class="divide-y divide-sn-border">
          <li
            v-for="item in queueItems"
            :key="item.id"
            class="flex flex-col sm:flex-row sm:items-center gap-3 py-3 first:pt-0 last:pb-0"
          >
            <button
              type="button"
              class="flex items-start gap-3 min-w-0 flex-1 text-left"
              @click="openConversation(item.id)"
            >
              <span
                class="grid place-content-center size-10 rounded-full bg-sn-primary/10 text-sn-primary font-semibold shrink-0"
              >
                {{ (item.name || '?').charAt(0).toUpperCase() }}
              </span>
              <span class="min-w-0 flex-1">
                <span class="flex items-center gap-2 flex-wrap">
                  <span class="font-medium text-sn-ink truncate">{{
                    item.name
                  }}</span>
                  <span
                    v-if="item.unread"
                    class="inline-grid place-items-center min-w-5 h-5 px-1 rounded-full bg-sn-accent text-white text-xxs font-medium"
                  >
                    {{ item.unread > 99 ? '99+' : item.unread }}
                  </span>
                </span>
                <span
                  v-if="item.phone"
                  class="block text-xs text-sn-muted truncate"
                  >{{ item.phone }}</span
                >
                <span class="block text-sm text-sn-muted truncate mt-0.5">{{
                  item.preview
                }}</span>
                <span
                  v-if="item.waitingLabel"
                  class="block text-xs text-sn-accent mt-0.5"
                  >{{ item.waitingLabel }}</span
                >
              </span>
            </button>
            <div class="flex items-center gap-2 shrink-0 sm:pl-2">
              <Button
                size="sm"
                color="slate"
                variant="faded"
                :label="t('AGENT_DESK.QUEUE.OPEN')"
                @click="openConversation(item.id)"
              />
              <Button
                size="sm"
                color="amber"
                solid
                :label="t('AGENT_DESK.QUEUE.ASSUME')"
                :is-loading="assuming && assumingId === item.id"
                @click="assumeConversation(item)"
              />
            </div>
          </li>
        </ul>
      </section>

      <!-- Middle widgets -->
      <section class="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div
          class="rounded-2xl bg-white border border-sn-border p-5 min-h-[220px]"
        >
          <div class="flex items-center justify-between gap-3 mb-4">
            <h2 class="font-semibold text-sn-ink">
              {{ t('AGENT_DESK.CSAT.TITLE') }}
            </h2>
            <span class="text-xs text-sn-muted">{{ t(periodLabelKey) }}</span>
          </div>
          <div
            v-if="!csatScore || !csatScore.rated"
            class="grid place-content-center h-40 text-sm text-sn-muted"
          >
            {{ t('AGENT_DESK.EMPTY') }}
          </div>
          <div v-else class="flex items-end gap-6">
            <p class="text-5xl font-semibold text-sn-primary">
              {{ csatScore.rated }}
            </p>
            <div class="text-sm text-sn-muted pb-2">
              <p>{{ t('AGENT_DESK.CSAT.RATED') }}</p>
              <p>
                {{
                  t('AGENT_DESK.CSAT.SENT', {
                    count: csatScore.sent,
                  })
                }}
              </p>
            </div>
          </div>
        </div>

        <div
          class="rounded-2xl bg-white border border-sn-border p-5 min-h-[220px]"
        >
          <div class="flex items-center justify-between gap-3 mb-4">
            <h2 class="font-semibold text-sn-ink">
              {{ t('AGENT_DESK.INTERACTIONS.TITLE') }}
            </h2>
            <div class="flex gap-1 rounded-lg bg-sn-bg p-1">
              <button
                v-for="p in ['daily', 'monthly', 'yearly']"
                :key="p"
                type="button"
                class="px-2.5 py-1 rounded-md text-xs font-medium transition"
                :class="
                  period === p
                    ? 'bg-white text-sn-primary shadow-sm'
                    : 'text-sn-muted'
                "
                @click="period = p"
              >
                {{ t(`AGENT_DESK.PERIOD.${p.toUpperCase()}`) }}
              </button>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-sn-muted">
                {{ t('AGENT_DESK.INTERACTIONS.CONVERSATIONS') }}
              </p>
              <p class="mt-1 text-3xl font-semibold text-sn-ink">
                {{ metrics.conversationsCount }}
              </p>
            </div>
            <div>
              <p class="text-sm text-sn-muted">
                {{ t('AGENT_DESK.INTERACTIONS.RESOLVED') }}
              </p>
              <p class="mt-1 text-3xl font-semibold text-sn-ink">
                {{ metrics.resolutionsCount }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Bottom widgets -->
      <section class="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div
          class="xl:col-span-2 rounded-2xl bg-white border border-sn-border p-5"
        >
          <h2 class="font-semibold text-sn-ink mb-4">
            {{ t('AGENT_DESK.STATUS_TIME.TITLE') }}
          </h2>
          <div class="flex flex-col md:flex-row gap-6 items-center">
            <div
              class="relative size-40 rounded-full"
              :style="{
                background: `conic-gradient(var(--sn-presence-online) 0 ${presence.onlinePct}%, var(--sn-presence-busy) 0 ${
                  presence.onlinePct + presence.busyPct
                }%, var(--sn-presence-away) 0 ${
                  presence.onlinePct + presence.busyPct + presence.awayPct
                }%, var(--sn-presence-offline) 0 100%)`,
              }"
            >
              <div
                class="absolute inset-4 rounded-full bg-white grid place-content-center text-center"
              >
                <p class="text-xs text-sn-muted">
                  {{ t('AGENT_DESK.STATUS_TIME.NOW') }}
                </p>
                <p class="text-sm font-semibold capitalize text-sn-ink">
                  {{ presence.status }}
                </p>
              </div>
            </div>
            <ul class="grid grid-cols-2 gap-x-8 gap-y-2 text-sm flex-1 w-full">
              <li class="flex justify-between gap-3">
                <span class="text-sn-muted">{{
                  t('AGENT_DESK.STATUS_TIME.ONLINE')
                }}</span>
                <span class="font-medium">{{ presence.online }} min</span>
              </li>
              <li class="flex justify-between gap-3">
                <span class="text-sn-muted">{{
                  t('AGENT_DESK.STATUS_TIME.AWAY')
                }}</span>
                <span class="font-medium">{{ presence.away }} min</span>
              </li>
              <li class="flex justify-between gap-3">
                <span class="text-sn-muted">{{
                  t('AGENT_DESK.STATUS_TIME.BUSY')
                }}</span>
                <span class="font-medium">{{ presence.busy }} min</span>
              </li>
              <li class="flex justify-between gap-3">
                <span class="text-sn-muted">{{
                  t('AGENT_DESK.STATUS_TIME.OFFLINE')
                }}</span>
                <span class="font-medium">{{ presence.offline }} min</span>
              </li>
              <li class="col-span-2 text-xs text-sn-muted pt-2">
                {{
                  t('AGENT_DESK.STATUS_TIME.TEAM', {
                    online: agentStatus?.online || 0,
                    busy: agentStatus?.busy || 0,
                    offline: agentStatus?.offline || 0,
                  })
                }}
              </li>
            </ul>
          </div>
        </div>

        <div class="rounded-2xl bg-white border border-sn-border p-5">
          <h2 class="font-semibold text-sn-ink mb-4">
            {{ t('AGENT_DESK.AVG_TIME.TITLE') }}
          </h2>
          <p class="text-4xl font-semibold text-sn-ink">
            {{ formatSeconds(metrics.avgReplyTime) }}
          </p>
          <p class="mt-2 text-sm text-sn-muted">
            {{ t('AGENT_DESK.AVG_TIME.REPLY') }}
          </p>
          <p class="mt-4 text-2xl font-semibold text-sn-ink">
            {{ formatSeconds(metrics.avgResolutionTime) }}
          </p>
          <p class="mt-1 text-sm text-sn-muted">
            {{ t('AGENT_DESK.AVG_TIME.RESOLUTION') }}
          </p>
        </div>
      </section>
    </div>
  </div>
</template>
